import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, List as ListIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', image: '', order: 0, active: true });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try { const r = await api.get('/admin/categories'); setCategories(r.data); }
        catch (e) { console.error(e); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ name: '', description: '', image: '', order: categories.length, active: true }); setDialogOpen(true); };
    const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, description: c.description || '', image: c.image || '', order: c.order || 0, active: c.active }); setDialogOpen(true); };

    const handleSave = async () => {
        if (!form.name) { toast.error('El nombre es obligatorio'); return; }
        setSaving(true);
        try {
            if (editing) { await api.put(`/admin/categories/${editing}`, form); toast.success('Categoría actualizada'); }
            else { await api.post('/admin/categories', form); toast.success('Categoría creada'); }
            setDialogOpen(false); load();
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta categoría? Esto podría afectar a los productos asociados.')) return;
        try { await api.delete(`/admin/categories/${id}`); toast.success('Categoría eliminada'); load(); }
        catch { toast.error('Error al eliminar'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-categories">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-heading font-bold text-cp-text-main">Categorías</h1>
                <button onClick={openNew} data-testid="add-category-btn" className="flex items-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-4 py-2.5 rounded-xl text-sm font-semibold">
                    <Plus size={16} /> Agregar
                </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {categories.map(c => (
                    <div key={c.id} data-testid={`category-card-${c.id}`} className="bg-white border border-cp-border rounded-xl p-4">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-cp-bg-alt flex items-center justify-center overflow-hidden border border-cp-border">
                                    {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <ListIcon className="text-cp-text-muted" size={18} />}
                                </div>
                                <div>
                                    <h3 className="font-heading font-bold text-cp-text-main">{c.name}</h3>
                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.active ? 'bg-cp-success/10 text-cp-success' : 'bg-cp-error/10 text-cp-error'}`}>
                                        {c.active ? 'Activa' : 'Inactiva'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => openEdit(c)} className="p-1.5 text-cp-text-muted hover:text-cp-secondary rounded-lg hover:bg-cp-bg-alt"><Pencil size={14} /></button>
                                <button onClick={() => handleDelete(c.id)} className="p-1.5 text-cp-text-muted hover:text-cp-error rounded-lg hover:bg-cp-bg-alt"><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <p className="text-cp-text-muted text-xs line-clamp-2 mb-2">{c.description || 'Sin descripción'}</p>
                        <div className="text-[10px] text-cp-text-muted font-mono">Slug: {c.slug}</div>
                    </div>
                ))}
                {!loading && categories.length === 0 && (
                    <div className="col-span-full text-center py-12 text-cp-text-muted">
                        No hay categorías registradas
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle className="font-heading">{editing ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-sm mb-1.5 block">Nombre *</Label>
                            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="ej: Juguetes" />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">Descripción</Label>
                            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Breve descripción..." />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">URL Imagen (opcional)</Label>
                            <Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} placeholder="https://..." />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">Orden de visualización</Label>
                            <Input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} />
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} />
                            <Label className="text-sm">Activa</Label>
                        </div>
                        <button onClick={handleSave} disabled={saving}
                            className="w-full bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white py-3 rounded-xl font-semibold">
                            {saving ? 'Guardando...' : editing ? 'Actualizar Categoría' : 'Crear Categoría'}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
